import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HostProfile, HostPronounType } from "@/types/database/host";
import { ISD_CODES } from "@/lib/constants";
import { activeCampaignService } from "@/lib/services/activeCampaign";

interface HostSignupFormProps {
  initialData?: HostProfile;
  session: any;
  profile: any;
}

const PRONOUNS: HostPronounType[] = ["He/Him", "She/Her", "They/Them", "Custom"];

export const HostSignupForm = ({ initialData, session, profile }: HostSignupFormProps) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<HostProfile>({
    defaultValues: {
      id: initialData?.id || "",
      full_name: initialData?.full_name || "",
      email: initialData?.email || "",
      isd_code: initialData?.isd_code || "",
      phone: initialData?.phone || "",
      pronouns: initialData?.pronouns || undefined,
      created_at: initialData?.created_at || new Date().toISOString(),
      updated_at: initialData?.updated_at || new Date().toISOString(),
    }
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Set initial values for select components and update them when initialData changes
  useEffect(() => {
    if (initialData) {
      // Update all form fields
      setValue("id", initialData.id);
      setValue("full_name", initialData.full_name);
      setValue("email", initialData.email);
      setValue("isd_code", initialData.isd_code);
      setValue("phone", initialData.phone);
      setValue("pronouns", initialData.pronouns);
      setValue("created_at", initialData.created_at);
      setValue("updated_at", initialData.updated_at);
    }
  }, [initialData, setValue]);

  const currentPronouns = watch("pronouns");
  const currentIsdCode = watch("isd_code");

  const onSubmit = async (data: HostProfile) => {
    if (!data.isd_code) {
      toast({
        title: "Validation Error",
        description: "Please select an ISD code",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Error",
          description: "No authenticated user found",
          variant: "destructive",
        });
        return;
      }

      const formData = {
        id: user.id,
        full_name: data.full_name,
        email: data.email,
        isd_code: data.isd_code,
        phone: data.phone,
        pronouns: data.pronouns,
        created_at: initialData?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as HostProfile;

      const { error: upsertError } = await supabase
        .from("host_profiles")
        .upsert(formData);

      if (!upsertError) {
        await supabase.auth.updateUser({
          data: {
            profile_completed: true
          }
        });
      } else throw upsertError;

      try {
        await activeCampaignService.onProfileComplete({
          email: user.email,
          firstName: data.full_name
        });
      } catch(error) {
        console.error('Error in onProfileComplete:', error);
      }

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <Label htmlFor="full_name">Full Name</Label>
        <Input
          id="full_name"
          {...register("full_name", { required: "Full name is required" })}
          className={errors.full_name ? "border-red-500" : ""}
        />
        {errors.full_name && (
          <p className="text-sm text-red-500 mt-1">{errors.full_name.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          })}
          className={errors.email ? "border-red-500" : ""}
        />
        {errors.email && (
          <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="isd_code">ISD Code</Label>
          <Select
            value={currentIsdCode}
            onValueChange={(value) => setValue("isd_code", value)}
          >
            <SelectTrigger className={errors.isd_code ? "border-red-500" : ""}>
              <SelectValue placeholder="Select ISD code" />
            </SelectTrigger>
            <SelectContent>
              {ISD_CODES.map((code) => (
                <SelectItem key={code.value} value={code.value}>
                  {code.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.isd_code && (
            <p className="text-sm text-red-500 mt-1">{errors.isd_code.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            {...register("phone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit phone number"
              }
            })}
            className={errors.phone ? "border-red-500" : ""}
          />
          {errors.phone && (
            <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="pronouns">Pronouns</Label>
        <Select
          value={currentPronouns}
          onValueChange={(value) => setValue("pronouns", value as HostPronounType)}
        >
          <SelectTrigger className={errors.pronouns ? "border-red-500" : ""}>
            <SelectValue placeholder="Select pronouns" />
          </SelectTrigger>
          <SelectContent>
            {PRONOUNS.map((pronoun) => (
              <SelectItem key={pronoun} value={pronoun}>
                {pronoun}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.pronouns && (
          <p className="text-sm text-red-500 mt-1">{errors.pronouns.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Updating..." : "Complete Profile"}
      </Button>
    </form>
  );
};